/**
 * Firebase initialization for admin app.
 *
 * Direct Firebase SDK usage — no shared infra layer.
 */
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import {
  getAuth,
  connectAuthEmulator,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  type User,
} from "firebase/auth";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  connectFirestoreEmulator,
  CACHE_SIZE_UNLIMITED,
  type Firestore,
} from "firebase/firestore";
import {
  getFunctions,
  connectFunctionsEmulator,
  httpsCallable,
} from "firebase/functions";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

/** Singleton Firebase App */
export const app =
  getApps().length > 0 ? getApp() : initializeApp(firebaseConfig);

/** App Check — required for Firebase Auth in production */
if (import.meta.env.DEV) {
  (self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN: string | boolean }).FIREBASE_APPCHECK_DEBUG_TOKEN =
    import.meta.env.VITE_FIREBASE_APPCHECK_DEBUG_TOKEN || true;
}

try {
  const siteKey = import.meta.env.VITE_FIREBASE_RECAPTCHA_ENTERPRISE_SITE_KEY;
  if (siteKey) {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  }
} catch (error) {
  console.error("[Admin] App Check initialization failed:", error);
}

/** Firebase Auth */
export const auth = getAuth(app);

/** Firestore with offline persistence */
let _db: Firestore | null = null;
export function getDb(): Firestore {
  if (_db) return _db;
  _db = initializeFirestore(app, {
    ignoreUndefinedProperties: true,
    localCache: persistentLocalCache({
      cacheSizeBytes: CACHE_SIZE_UNLIMITED,
      tabManager: persistentMultipleTabManager(),
    }),
  });
  return _db;
}
export const db = getDb();

/**
 * Cloud Functions.
 *
 * Регион обязателен: весь бэкенд переехал в europe-central2, а us-central1
 * (дефолт SDK) снесён при миграции регионов. Без явного региона callable `api`
 * бил бы в мёртвый us-central1 → 404 на любой админский вызов.
 */
export const functions = getFunctions(app, "europe-central2");

// Connect to emulators in development
if (import.meta.env.DEV) {
  const host = import.meta.env.VITE_EMULATOR_HOST || "127.0.0.1";

  // Порты читаем из env с фоллбэком на дефолты firebase.json. Это позволяет
  // параллельным git worktree указывать на свой изолированный стек эмуляторов
  // (см. scripts/wt-dev.sh); прод/обычный dev без env работают как раньше.
  const port = (value: string | undefined, fallback: number): number => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
  };
  const authPort = port(import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_PORT, 9099);
  const firestorePort = port(
    import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_PORT,
    8080
  );
  const functionsPort = port(
    import.meta.env.VITE_FIREBASE_FUNCTIONS_EMULATOR_PORT,
    5001
  );

  connectAuthEmulator(auth, `http://${host}:${authPort}`, {
    disableWarnings: true,
  });
  connectFirestoreEmulator(db, host, firestorePort);
  connectFunctionsEmulator(functions, host, functionsPort);

  console.log(`[Admin] Connected to Firebase emulators at ${host}`);
}

// Re-export for convenience
export {
  signInWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
  httpsCallable,
};
export type { User };
